/**
 * contractStyles.ts - Constantes e CSS compartilhados entre Editor, Preview e PDF
 * 
 * Centraliza dimensões A4, paddings e tipografia para garantir paridade
 * visual 100% entre os três contextos (Editor, Preview, PDF via html2pdf.app).
 * 
 * IMPORTANTE: O html2pdf.app usa Chromium headless a 96 DPI internamente.
 * Browsers modernos também usam 96 DPI. Portanto, 1mm = 3.7795px em ambos.
 * Usamos mm como unidade absoluta para o PDF e seus equivalentes em px
 * (a 96 DPI) para o Preview/Editor, eliminando discrepâncias de DPI.
 * 
 * RELEASE: v2.1.0 - 2026-09-09 - Dimensões A4 e conversão mm↔px passam a vir de
 *                                 src/lib/document/pageMetrics.ts (fonte única compartilhada
 *                                 com o Studio). Valores e comportamento inalterados.
 * RELEASE: v2.0.0 - 2026-02-16 - Migração para 96 DPI e unidades mm
 * RELEASE: v1.0.0 - 2026-02-16 - Arquivo criado para unificar estilos
 */

// Primitivas neutras de página (compartilhadas com o Studio e geradores de PDF)
import {
  A4_WIDTH_MM,
  A4_HEIGHT_MM,
  A4_WIDTH_PX,
  A4_HEIGHT_PX,
  mmToPx,
} from '@/lib/document/pageMetrics';

// Reexporta para manter 100% de compatibilidade com os imports já existentes
export { A4_WIDTH_MM, A4_HEIGHT_MM, A4_WIDTH_PX, A4_HEIGHT_PX, mmToPx };

// =============================================================================
// MARGENS ESPECÍFICAS DO CONTRATO (em milímetros)
// =============================================================================

/** Margem lateral em mm (esquerda e direita) */
export const MARGIN_LATERAL_MM = 14;

/** Margem topo SEM imagem de fundo (mm) */
export const MARGIN_TOP_NO_BG_MM = 14;

/** Margem inferior SEM imagem de fundo (mm) */
export const MARGIN_BOTTOM_NO_BG_MM = 14;

/** Margem topo COM imagem de fundo (mm) */
export const MARGIN_TOP_WITH_BG_MM = 21;

/** Margem inferior COM imagem de fundo (mm) */
export const MARGIN_BOTTOM_WITH_BG_MM = 28;


/** Margem lateral em px (96 DPI) — 14mm ≈ 53px */
export const MARGIN_LATERAL_PX = mmToPx(MARGIN_LATERAL_MM); // 53

/** Largura útil do conteúdo em px (96 DPI) = A4 - 2x margem lateral */
export const CONTENT_WIDTH_PX = A4_WIDTH_PX - (MARGIN_LATERAL_PX * 2); // ~688

// =============================================================================
// FUNÇÕES DE PADDING (retornam strings CSS para uso direto)
// =============================================================================

/**
 * Retorna string de padding em MILÍMETROS para o CSS do PDF (Edge Function).
 * Com background: '21mm 14mm 28mm 14mm'
 * Sem background: '14mm'
 */
export function getPagePaddingMM(hasBackground: boolean): string {
  return hasBackground
    ? `${MARGIN_TOP_WITH_BG_MM}mm ${MARGIN_LATERAL_MM}mm ${MARGIN_BOTTOM_WITH_BG_MM}mm ${MARGIN_LATERAL_MM}mm`
    : `${MARGIN_TOP_NO_BG_MM}mm`;
}

/**
 * Retorna string de padding em PIXELS (96 DPI) para Preview e Editor no browser.
 * Com background: '79px 53px 106px 53px'
 * Sem background: '53px'
 * @param bgMarginTopMm - Margem superior em mm (padrão: constante MARGIN_TOP_WITH_BG_MM)
 * @param bgMarginBottomMm - Margem inferior em mm (padrão: constante MARGIN_BOTTOM_WITH_BG_MM)
 */
export function getPagePaddingPX(
  hasBackground: boolean,
  showPageNumbers?: boolean,
  pageNumberPosition?: string,
  bgMarginTopMm?: number,
  bgMarginBottomMm?: number,
): string {
  if (hasBackground) {
    return `${mmToPx(bgMarginTopMm ?? MARGIN_TOP_WITH_BG_MM)}px ${MARGIN_LATERAL_PX}px ${mmToPx(bgMarginBottomMm ?? MARGIN_BOTTOM_WITH_BG_MM)}px ${MARGIN_LATERAL_PX}px`;
  }
  // Sem background + paginação: margem extra de 21mm (79px) no lado do footer/header
  if (showPageNumbers) {
    const topPx = pageNumberPosition === 'top-right' ? mmToPx(21) : mmToPx(MARGIN_TOP_NO_BG_MM);
    const bottomPx = pageNumberPosition === 'top-right' ? mmToPx(MARGIN_BOTTOM_NO_BG_MM) : mmToPx(21);
    return `${topPx}px ${MARGIN_LATERAL_PX}px ${bottomPx}px ${MARGIN_LATERAL_PX}px`;
  }
  return `${mmToPx(MARGIN_TOP_NO_BG_MM)}px`;
}

/**
 * Mantém compatibilidade com chamadas existentes que usam getPagePadding.
 * Agora retorna valores em px a 96 DPI (equivalentes exatos aos mm do PDF).
 * @param bgMarginTopMm - Margem superior em mm (padrão: constante MARGIN_TOP_WITH_BG_MM)
 * @param bgMarginBottomMm - Margem inferior em mm (padrão: constante MARGIN_BOTTOM_WITH_BG_MM)
 */
export function getPagePadding(
  hasBackground: boolean,
  showPageNumbers?: boolean,
  pageNumberPosition?: string,
  bgMarginTopMm?: number,
  bgMarginBottomMm?: number,
): string {
  return getPagePaddingPX(hasBackground, showPageNumbers, pageNumberPosition, bgMarginTopMm, bgMarginBottomMm);
}

// =============================================================================
// FUNÇÃO DE ALTURA ÚTIL
// =============================================================================

/**
 * Retorna a altura útil por página em px (96 DPI), descontando padding vertical.
 * Usada para calcular número de páginas e posição de quebras no Preview/Editor.
 * @param bgMarginTopMm - Margem superior em mm (padrão: constante MARGIN_TOP_WITH_BG_MM)
 * @param bgMarginBottomMm - Margem inferior em mm (padrão: constante MARGIN_BOTTOM_WITH_BG_MM)
 */
export function getUsableHeight(
  hasBackground: boolean,
  showPageNumbers?: boolean,
  pageNumberPosition?: string,
  bgMarginTopMm?: number,
  bgMarginBottomMm?: number,
): number {
  if (hasBackground) {
    return A4_HEIGHT_PX
      - mmToPx(bgMarginTopMm ?? MARGIN_TOP_WITH_BG_MM)
      - mmToPx(bgMarginBottomMm ?? MARGIN_BOTTOM_WITH_BG_MM);
  }
  // Sem background + paginação: margem extra de 21mm (79px) para o footer/header template
  if (showPageNumbers) {
    const topPx = pageNumberPosition === 'top-right' ? mmToPx(21) : mmToPx(MARGIN_TOP_NO_BG_MM);
    const bottomPx = pageNumberPosition === 'top-right' ? mmToPx(MARGIN_BOTTOM_NO_BG_MM) : mmToPx(21);
    return A4_HEIGHT_PX - topPx - bottomPx;
  }
  return A4_HEIGHT_PX - mmToPx(MARGIN_TOP_NO_BG_MM) - mmToPx(MARGIN_BOTTOM_NO_BG_MM);
}

// =============================================================================
// FUNÇÃO DE PADDING SUPERIOR (valor numérico para cálculos de posicionamento)
// =============================================================================

/**
 * Retorna o padding superior em pixels (96 DPI) como valor numérico.
 * Usado pelo Editor para posicionar corretamente os indicadores de quebra de página.
 * @param bgMarginTopMm - Margem superior em mm (padrão: constante MARGIN_TOP_WITH_BG_MM)
 */
export function getTopPaddingPX(
  hasBackground: boolean,
  showPageNumbers?: boolean,
  pageNumberPosition?: string,
  bgMarginTopMm?: number,
): number {
  if (hasBackground) return mmToPx(bgMarginTopMm ?? MARGIN_TOP_WITH_BG_MM);
  if (showPageNumbers && pageNumberPosition === 'top-right') return mmToPx(21);
  return mmToPx(MARGIN_TOP_NO_BG_MM);
}

// =============================================================================
// ESCALONAMENTO VISUAL (para caber no dialog/modal)
// =============================================================================

/**
 * Fator de escala LEGADO (~0.749) para exibir a página A4 (794px) dentro de
 * ~595px no UI. Mantido para compatibilidade. Em containers responsivos,
 * prefira `getPreviewScaleFactor(containerWidth)` que se adapta a mobile.
 *
 * @deprecated Use `getPreviewScaleFactor` em novos componentes responsivos.
 */
export const PREVIEW_SCALE_FACTOR = 595 / A4_WIDTH_PX; // ≈ 0.749

/**
 * Calcula um fator de escala dinâmico baseado na largura disponível do
 * container, garantindo que a página A4 (794px @ 96 DPI) caiba inteiramente
 * sem scroll horizontal — incluindo em viewports mobile.
 *
 * - Limita a escala a 1 (não amplia além do tamanho real).
 * - Estabelece um piso mínimo (0.32) para manter legibilidade em telas
 *   muito estreitas.
 * - Subtrai um padding lateral (24px por lado) que o container scrollável
 *   reserva para "moldura".
 *
 * @param containerWidthPx Largura do container externo em pixels.
 * @param paddingPx Padding lateral total considerado (default 48 = 24*2).
 * @returns Fator entre ~0.32 e 1.
 */
export function getPreviewScaleFactor(
  containerWidthPx: number,
  paddingPx = 48,
): number {
  if (!containerWidthPx || containerWidthPx <= 0) return PREVIEW_SCALE_FACTOR;
  const usable = Math.max(0, containerWidthPx - paddingPx);
  const raw = usable / A4_WIDTH_PX;
  return Math.min(1, Math.max(0.32, raw));
}

// =============================================================================
// CSS BASE DO CONTEÚDO DO CONTRATO
// =============================================================================

/**
 * CSS base para elementos HTML do conteúdo do contrato.
 * Aplicado no Editor (via <style>), Preview (via <style>) e PDF (inline na Edge Function).
 * Garante que p, h1, h2, h3, listas, tabelas, etc. tenham margens e espaçamentos idênticos.
 * 
 * NOTA: Tipografia em pt é DPI-independente (1pt = 1/72 inch em qualquer contexto).
 */
export const CONTRACT_CONTENT_CSS = `
  .contract-content {
    font-family: Arial, sans-serif;
    font-size: 12pt;
    line-height: 1.6;
    color: #333333;
    word-break: break-word;
    overflow-wrap: break-word;
  }
  .contract-content p { margin: 0 0 1em 0; min-height: 1.6em; }
  .contract-content p:empty { min-height: 1.6em; display: block; }
  .contract-content h1 { font-size: 18pt; font-weight: bold; margin: 0.67em 0; }
  .contract-content h2 { font-size: 14pt; font-weight: bold; margin: 0.83em 0; }
  .contract-content h3 { font-size: 12pt; font-weight: bold; margin: 1em 0; }
  .contract-content ul, .contract-content ol { margin: 0 0 1em 0; padding-left: 2em; }
  .contract-content li { margin: 0.25em 0; }
  .contract-content hr { border: none; border-top: 1px solid #ddd; margin: 1.5em 0; }
  .contract-content table { width: 100%; border-collapse: collapse; margin: 1em 0; }
  .contract-content th, .contract-content td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  .contract-content a { color: #2563eb; text-decoration: underline; }
  .contract-content img { max-width: 100%; height: auto; display: block; }
`;
